// Pure 12-column dashboard layout engine: collision detection, vertical
// compaction, move/resize with push-down, and first-fit placement for new
// widgets. Free of React and the DOM so the whole drag model is unit-testable;
// the dashboard-local widget grid translates pointer events into these calls
// and renders the result.

export const GRID_COLUMNS = 12;
export const MAX_WIDGET_ROWS = 12;

export interface WidgetLayout {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface LayoutItem {
  id: string;
  layout: WidgetLayout;
}

export function collides(a: WidgetLayout, b: WidgetLayout): boolean {
  return (
    a.x < b.x + b.width &&
    b.x < a.x + a.width &&
    a.y < b.y + b.height &&
    b.y < a.y + a.height
  );
}

function collidesWithAny(
  layout: WidgetLayout,
  placed: readonly LayoutItem[],
): boolean {
  return placed.some((item) => collides(layout, item.layout));
}

// Clamp a candidate layout onto the grid (never off the right edge, never
// negative, height capped).
export function clampLayout(layout: WidgetLayout): WidgetLayout {
  const width = Math.min(Math.max(layout.width, 1), GRID_COLUMNS);
  const height = Math.min(Math.max(layout.height, 1), MAX_WIDGET_ROWS);
  const x = Math.min(Math.max(layout.x, 0), GRID_COLUMNS - width);
  const y = Math.max(layout.y, 0);
  return { x, y, width, height };
}

// Stable reading order: top-to-bottom, then left-to-right. Narrow viewports
// stack tiles in exactly this order.
export function readingOrder(items: readonly LayoutItem[]): LayoutItem[] {
  return [...items].sort(
    (first, second) =>
      first.layout.y - second.layout.y || first.layout.x - second.layout.x,
  );
}

// Pull every item as far up as it can go (in reading order) without
// collisions. Preserves x positions; only y moves.
export function compact(items: readonly LayoutItem[]): LayoutItem[] {
  const placed: LayoutItem[] = [];
  for (const item of readingOrder(items)) {
    let y = item.layout.y;
    while (y > 0 && !collidesWithAny({ ...item.layout, y: y - 1 }, placed)) {
      y -= 1;
    }
    while (collidesWithAny({ ...item.layout, y }, placed)) {
      y += 1;
    }
    placed.push({ id: item.id, layout: { ...item.layout, y } });
  }
  const byId = new Map(placed.map((item) => [item.id, item]));
  return items.map((item) => byId.get(item.id) ?? item);
}

// Place `movedId` at `target`, push colliding items down below it, then
// compact everything (the moved item too — dropping into empty space snaps
// up). This is the single entry point for both drag-move and drag-resize.
export function applyPlacement(
  items: readonly LayoutItem[],
  movedId: string,
  target: WidgetLayout,
): LayoutItem[] {
  const moved = items.find((item) => item.id === movedId);
  if (!moved) {
    return [...items];
  }
  const clamped = clampLayout(target);
  const placed: LayoutItem[] = [{ id: movedId, layout: clamped }];
  for (const item of readingOrder(items)) {
    if (item.id === movedId) {
      continue;
    }
    let y = item.layout.y;
    while (collidesWithAny({ ...item.layout, y }, placed)) {
      y += 1;
    }
    placed.push({ id: item.id, layout: { ...item.layout, y } });
  }
  return compact(
    items.map((item) => placed.find((next) => next.id === item.id) ?? item),
  );
}

// First-fit position for a new widget of the given size: scan rows top-down,
// columns left-to-right, and take the first free slot (worst case: below
// everything).
export function placeNew(
  items: readonly LayoutItem[],
  requestedWidth: number,
  requestedHeight: number,
): WidgetLayout {
  const width = Math.min(Math.max(requestedWidth, 1), GRID_COLUMNS);
  const height = Math.min(Math.max(requestedHeight, 1), MAX_WIDGET_ROWS);
  const maxY = items.reduce(
    (bottom, item) => Math.max(bottom, item.layout.y + item.layout.height),
    0,
  );
  for (let y = 0; y <= maxY; y += 1) {
    for (let x = 0; x <= GRID_COLUMNS - width; x += 1) {
      const candidate = { x, y, width, height };
      if (!collidesWithAny(candidate, [...items])) {
        return candidate;
      }
    }
  }
  return { x: 0, y: maxY, width, height };
}

// Total rows the grid needs to contain every item (min 1 so an empty board
// still renders a drop target row in edit mode).
export function gridRows(items: readonly LayoutItem[]): number {
  return Math.max(
    1,
    ...items.map((item) => item.layout.y + item.layout.height),
  );
}
